import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { playSoundEffect } from './hooks/useSoundEffects';
import { useContainerScale } from './hooks/useViewportScale';
import { API_URL } from './config/apiConfig';
import './Tutorial.css';

const compareMissionIds = (mission1, mission2) => {
  const parseMission = (missionId) => {
    if (!missionId) return { chapter: 0, mission: 0 };
    const match = missionId.match(/chapter_(\d+)_mission_(\d+)/);
    if (!match) return { chapter: 0, mission: 0 };
    return { chapter: parseInt(match[1], 10), mission: parseInt(match[2], 10) };
  };
  const m1 = parseMission(mission1);
  const m2 = parseMission(mission2);
  if (m1.chapter !== m2.chapter) return m1.chapter - m2.chapter;
  return m1.mission - m2.mission;
};

const TUTORIAL_ITEMS = [
  { id: 'tutorial1', missionId: 'chapter_1_mission_1', image: 'tutorial1.jpg', titleKey: 'tutorial.tutorial1', descriptionKey: 'tutorial.description1' },
  { id: 'tutorial2', missionId: 'chapter_1_mission_2', image: 'tutorial2.jpg', titleKey: 'tutorial.tutorial2', descriptionKey: 'tutorial.description2' },
  { id: 'tutorial3', missionId: 'chapter_1_mission_3', image: 'tutorial3.jpg', titleKey: 'tutorial.tutorial3', descriptionKey: 'tutorial.description3' },
  { id: 'tutorial4', missionId: 'chapter_1_mission_4', image: 'tutorial4.jpg', titleKey: 'tutorial.tutorial4', descriptionKey: 'tutorial.description4' },
];

function Tutorial({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [campaignProgress, setCampaignProgress] = useState(null);

  const {
    scale: viewportScale,
    offsetX: viewportOffsetX,
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  useEffect(() => {
    const fetchCampaignProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await fetch(`${API_URL}/campaign/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCampaignProgress(data.latest_campaign || null);
        }
      } catch (error) {
        console.error('Error fetching campaign progress:', error);
      }
    };
    fetchCampaignProgress();
  }, []);

  const isMissionCompleted = (missionId) => {
    if (!missionId || !campaignProgress) return false;
    return compareMissionIds(campaignProgress, missionId) >= 0;
  };

  const handleBoxClick = (item) => {
    if (!item.missionId) return; // Missions 3 and 4 not implemented yet
    playSoundEffect('/sounds/click.mp3');
    navigate(`/campaign-battle/${item.missionId}`);
  };

  return (
    <div className="tutorial-viewport-wrapper">
      <div
        className="tutorial-fullscreen-background"
        style={{ backgroundImage: 'url(/images/tutorial.jpg)' }}
      />
      <div
        className={`tutorial-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="tutorial-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              navigate('/battleoverview');
            }}
            className="tutorial-back-button"
          >
            {t('common.back')}
          </button>

          <div className="tutorial-page-content">
            <div className="tutorial-boxes">
              {TUTORIAL_ITEMS.map((item, index) => {
                const completed = item.missionId ? isMissionCompleted(item.missionId) : false;
                const clickable = !!item.missionId;
                return (
                  <React.Fragment key={item.id}>
                    {index > 0 && <div className="tutorial-box-connector" aria-hidden="true" />}
                    <div
                      className={`tutorial-box ${clickable ? 'tutorial-box-clickable' : ''} ${completed ? 'tutorial-box-completed' : ''}`}
                      onClick={() => handleBoxClick(item)}
                      role={clickable ? 'button' : undefined}
                    >
                      <div className="tutorial-box-image" style={{ backgroundImage: `url(/images/${item.image})` }} />
                      <h3 className="tutorial-box-title">{t(item.titleKey)}</h3>
                      <p className="tutorial-box-description">{t(item.descriptionKey)}</p>
                      {completed && (
                        <div className="tutorial-box-check" aria-label={t('tutorial.completed')}>
                          ✓
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tutorial;
